import type { CommercialTermsData } from '../store/rfqSlice';
import type { CriteriaPointer } from '../types/criteriaPointers';
import { prioritiesFromBackend } from './sourcingCriteria';

/**
 * The sourcing criteria as short chips for the Smart BOM: the buyer's
 * structured step-2 answers (neutral) and Robbie's reading of their notes
 * (Robbie tone). A field the buyer left empty has no chip — never a
 * placeholder like "Anywhere" standing in for an answer nobody gave.
 */
export interface CriteriaChip {
  key: string;
  /** Muted prefix ("Deliver to"); absent on Robbie's pointer chips. */
  label?: string;
  value: string;
  /** Hover text: the full value behind a shortened one, or the note quoted. */
  title?: string;
  /** Free text that may run long — one line with an ellipsis, full text in `title`. */
  truncate?: boolean;
}

/** What "Ask Robbie to change" sends on the buyer's behalf. */
export const CHANGE_CRITERIA_MESSAGE = "I'd like to change my sourcing criteria";

const TOP_PRIORITIES = 3;

/**
 * The town a delivery address names, for a chip: "Hinjewadi, Pune,
 * Maharashtra 411057, India" → "Pune". With three or more parts the last is
 * the country and the one before it the state, so the town sits just ahead
 * of the state; one or two parts lead with the town.
 */
export const shortPlace = (address: string): string => {
  const parts = address.split(',').map((part) => part.trim()).filter(Boolean);
  if (parts.length <= 2) return parts[0] || '';
  const withoutCountry = parts.slice(0, -1);
  return withoutCountry[withoutCountry.length - 2];
};

/** Top ranked priorities, "Price › Quality › Reliability"; '' when none were saved. */
const priorityText = (saved: string): string => {
  if (!saved) return '';
  try {
    const parsed = JSON.parse(saved);
    if (!Array.isArray(parsed) || parsed.length === 0) return '';
    return prioritiesFromBackend(parsed)
      .slice(0, TOP_PRIORITIES)
      .map((option) => option.name)
      .join(' › ');
  } catch {
    return '';
  }
};

export const structuredCriteriaChips = (terms: CommercialTermsData): CriteriaChip[] => {
  const chips: CriteriaChip[] = [];
  const delivery = terms.deliveryLocation.trim();
  if (delivery) {
    chips.push({ key: 'deliver-to', label: 'Deliver to', value: shortPlace(delivery), title: delivery });
  }
  const leadTime = terms.desiredLeadTime.trim();
  if (leadTime) chips.push({ key: 'lead-time', label: 'Lead time', value: leadTime });
  const compliance = terms.complianceRequirements.filter((value) => value.trim());
  if (compliance.length > 0) {
    chips.push({ key: 'compliance', label: 'Compliance', value: compliance.join(' · ') });
  }
  const priority = priorityText(terms.supplierPriority);
  if (priority) chips.push({ key: 'priority', label: 'Priority', value: priority });
  const sourcing = terms.sourcingLocation.trim();
  if (sourcing) chips.push({ key: 'source-from', label: 'Source from', value: sourcing });
  return chips;
};

/**
 * The buyer's notes as they wrote them, for when Robbie's pointers are not
 * there (still being read, or unreadable) — so the notes are never invisible.
 */
export const notesChip = (notes: string): CriteriaChip[] => {
  const text = notes.replace(/\s+/g, ' ').trim();
  return text ? [{ key: 'notes', label: 'Your notes', value: text, title: notes.trim(), truncate: true }] : [];
};

/** One chip per distinct pointer label, quoting the note it came from. */
export const pointerChips = (pointers: CriteriaPointer[]): CriteriaChip[] => {
  const seen = new Set<string>();
  return pointers.flatMap((pointer, index) => {
    const label = pointer.label.trim();
    if (!label || seen.has(label.toLowerCase())) return [];
    seen.add(label.toLowerCase());
    const quote = pointer.quote.trim();
    return [{
      key: `note-${index}`,
      value: label,
      title: quote ? `From your notes: “${quote}”` : undefined,
    }];
  });
};
